import { useCallback, useEffect, useState } from 'react';

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

// iOS Safari never fires `beforeinstallprompt`, so it needs a manual
// "Share -> Add to Home Screen" nudge instead of a one-tap install button.
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

// Shared across every component instance so the captured browser prompt
// event isn't lost if the first listener mounts after it already fired.
let deferredPromptSingleton = null;
const listeners = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPromptSingleton = event;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferredPromptSingleton = null;
    listeners.forEach((fn) => fn());
  });
}

export const usePWAInstall = () => {
  const [installed, setInstalled] = useState(isStandalone());
  const [available, setAvailable] = useState(Boolean(deferredPromptSingleton));

  useEffect(() => {
    const sync = () => {
      setAvailable(Boolean(deferredPromptSingleton));
      setInstalled(isStandalone());
    };
    listeners.add(sync);
    sync();
    return () => listeners.delete(sync);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPromptSingleton) return 'unavailable';
    deferredPromptSingleton.prompt();
    const { outcome } = await deferredPromptSingleton.userChoice;
    deferredPromptSingleton = null;
    listeners.forEach((fn) => fn());
    return outcome;
  }, []);

  return {
    canInstall: available && !installed,
    // Nothing to prompt, but still worth nudging with manual steps.
    needsManualInstall: isIOS() && !installed && !available,
    installed,
    promptInstall,
  };
};
